import React, { useState, useMemo } from 'react';
import { useRouter } from 'expo-router';
import { useAuthStore, useUIStore, useChatInputStore, useModelStore, useCoworkerStore, useChatStore } from '@/store';
import { findTaskIdForConversation, taskIdFromChat } from '@/store/coworker';
import { useTranslation } from '@/hooks/useTranslation';
import NavigationBarButtonContainer from '@/components/Navigation/NavigationBarButtonContainer';
import NavigationBarButton from '@/components/Navigation/NavigationBarButton';
import Button from '@/components/Buttons/Button';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { Image } from 'expo-image';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useChat } from '@/hooks/useChat';
import { openNewChatComposer } from '@/lib/openHomeComposer';
import { displayChatTitle } from '@/lib/chatTitle';
import { getChatHeaderTitleMaxWidth } from '@/constants/utils/chat_header_title';
import { getNavigationSidebarWidth, getEffectiveContextSidebarWidth, isArtifactSidebarType } from '@/constants/config';
import { useAuthPresentation } from '@/hooks/useRequireAuth';
import DefaultRouteNavigationHeaderLeft from '@/components/Navigation/Routes/DefaultRouteNavigationHeaderLeft';
import ChatDropdown from '@/components/Dropdowns/ChatDropdown';
import { View, Pressable, StyleSheet, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useModalStore } from '@/store';

const EMPTY_MODEL_IDS = Object.freeze([]);

const createStyles = (colors) => StyleSheet.create({
  headerText: {
    fontSize: 18,
    fontFamily: 'ui-sans-serif,-apple-system, system-ui, -apple-system, BlinkMacSystemFont, Roboto, Ubuntu, "Open Sans", Arial, "Helvetica Neue", Segoe UI Emoji, Segoe UI Symbol, sans-serif',
    fontWeight: 400,
    color: colors.text_color_primary,
    overflow: 'hidden',
    flexShrink: 1,
    minWidth: 0,
    textAlign: 'center',
    ...(Platform.OS === 'web' ? {
      display: 'block',
      width: '100%',
      maxWidth: '100%',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    } : {}),
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 0,
    flexShrink: 1,
    overflow: 'hidden',
  },
  chevronContainer: {
    height: 11,
    width: 11,
    marginLeft: 4,
    marginTop: 1,
  },
  // Model selector styles
  modelSelectorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    backgroundColor: colors.solid_color_secondary,
    minWidth: 80,
    maxWidth: 200,
    overflow: 'hidden',
    flexShrink: 1,
  },
  modelSelectorContainerHovered: {
    backgroundColor: colors.solid_color_tertiary,
  },
  modelImage: {
    width: 20,
    height: 20,
    borderRadius: 0,
    backgroundColor: '#fff',
  },
  modelName: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.text_color_primary,
    maxWidth: 100,
  },
  chevron: {
    marginLeft: 2,
  },
});

// Model selector component for the header
const HeaderModelSelector = React.memo(({ conversationId }) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = createStyles(colors);
  const titleMaxWidth = useChatHeaderTitleMaxWidth();
  const { openModal } = useModalStore(state => state.actions);
  const setContext = useChatInputStore(state => state.setContext);
  const setSelectedModels = useChatInputStore(state => state.setSelectedModels);
  
  // Get selected model IDs for the current context
  const contextId = conversationId || 'new';
  const selectedModelIds = useChatInputStore(state =>
    state.inputStates?.[contextId]?.selectedModelIds ?? EMPTY_MODEL_IDS
  );
  
  // Get model data from model store
  const models = useModelStore(state => state.models);
  
  // Get selected model objects
  const selectedModels = useMemo(() => {
    if (!selectedModelIds || selectedModelIds.length === 0) return [];
    return selectedModelIds
      .map(id => {
        // Handle both string IDs and model objects
        const modelId = typeof id === 'string' ? id : id?.id;
        return models[modelId];
      })
      .filter(Boolean);
  }, [selectedModelIds, models]);
  
  const isAutoMode = selectedModels.length === 0;
  const displayName = isAutoMode 
    ? t('nav.routes.home.auto') 
    : selectedModels.length === 1 
      ? selectedModels[0]?.name || t('nav.routes.chat.modelFallback')
      : `${selectedModels.length} ${t('nav.routes.home.modelsCount')}`;
  
  const handlePress = () => {
    openModal('ModelSelectorDetailedModal', {
      modelIds: selectedModelIds,
      onChange: (modelIds) => {
        // Ensure we update the correct context (same as ChatInput does)
        setContext(contextId);
        setSelectedModels(modelIds);
      },
      maxModels: 5,
      noMultiSelectMode: false,
      showDoneButton: true,
      showSelectedModels: true,
    });
  };
  
  return (
    <Pressable 
      style={({ hovered }) => [
        styles.modelSelectorContainer,
        { maxWidth: Math.min(200, titleMaxWidth) },
        hovered && styles.modelSelectorContainerHovered
      ]}
      onPress={handlePress}
    >
      {selectedModels.length > 0 && selectedModels[0]?.logoPath ? (
        <Image
          source={{ uri: selectedModels[0].logoPath }}
          style={styles.modelImage}
        />
      ) : null}
      <Text style={styles.modelName} numberOfLines={1}>
        {displayName}
      </Text>
      <Icon
        name="chevron-down"
        size={12}
        color={colors.text_color_tertiary}
        style={styles.chevron}
      />
    </Pressable>
  );
});

HeaderModelSelector.displayName = 'HeaderModelSelector';

const isDesktopPlatform = Platform.OS === 'web' || Platform.OS === 'macos';
const isNativeApp = Platform.OS === 'ios' || Platform.OS === 'android';

const ThemedHeaderBackground = React.memo(() => {
  const { colors } = useTheme();
  return <View style={{ flex: 1, backgroundColor: colors.solid_color_primary }} />;
});

ThemedHeaderBackground.displayName = 'ThemedHeaderBackground';

function useChatHeaderTitleMaxWidth() {
  const { isXS, isSM, windowWidth } = useResponsiveness();
  const navigationSidebarWidth = useUIStore(state => getNavigationSidebarWidth(state, windowWidth));
  const isContextSidebarVisible = useUIStore(state => state.isContextSidebarVisible);
  const contextSidebarWidth = useUIStore(state => getEffectiveContextSidebarWidth(state, windowWidth));

  return getChatHeaderTitleMaxWidth({
    windowWidth,
    navigationSidebarWidth: isXS ? 0 : navigationSidebarWidth,
    contextSidebarWidth: !isXS && isContextSidebarVisible ? (contextSidebarWidth || 0) : 0,
    compactActions: isXS || isSM,
  });
}

const HeaderTitle = React.memo(({ chat, isTemporary, children, conversationId, isLinkedWork = false, preferMobileHeader }) => {
  const { t } = useTranslation();
  const { isXS, windowWidth } = useResponsiveness();
  const isOwner = chat?.isOwner;
  const { colors } = useTheme();
  const styles = createStyles(colors);
  const titleMaxWidth = useChatHeaderTitleMaxWidth();
  const [isOpen, setIsOpen] = useState(false);
  const requestScrollToTop = useUIStore(state => state.requestScrollToTop);
  const hideForArtifact = useUIStore(state => isDesktopPlatform
    && state.isContextSidebarVisible && isArtifactSidebarType(state.contextSidebarType)
    && (Platform.OS !== 'macos' || getEffectiveContextSidebarWidth(state, windowWidth) > 0));
  const displayTitle = displayChatTitle(chat?.title, typeof chat?.title === 'string' ? chat.title.trim() : chat?.title);
  // Some conversation-list payloads omit flags while still carrying the
  // canonical task link. Either signal is enough to keep Work out of normal
  // chat/model navigation while the full conversation hydrates.
  const isWork = isLinkedWork || !!(chat?.flags?.isCoworkerSession || chat?.coworkerSession);

  // Work chooses its own model step-by-step. Showing Auto/model controls in a
  // Work thread implies a setting the user can change when they cannot.
  // Compact native headers float over the transcript, so keep the title out of
  // that layer and leave the actions in the glass controls.
  // Composer hides the models button when isXS, so the centered header pill is
  // the only switcher. Temporary chats used to show a "Temporary Chat" label
  // instead. Keep that label on desktop web; on native (and any layout that
  // opts into the mobile header) always use the same pill as a normal chat.
  const showHeaderModelSelector = !isWork && !isXS && (isTemporary && (isNativeApp || preferMobileHeader));
  const renderClampedTitle = (label, extraProps) => (
    <View style={[styles.headerContainer, { maxWidth: titleMaxWidth, width: titleMaxWidth }, Platform.OS === 'macos' && { maxWidth: '100%', width: '100%' }]}>
      <Text
        tag={Platform.OS === 'web' ? 'span' : undefined}
        style={styles.headerText}
        numberOfLines={1}
        ellipsizeMode="tail"
        {...extraProps}
      >
        {label}
      </Text>
    </View>
  );

  if (hideForArtifact) return null;

  if (showHeaderModelSelector) {
    return <HeaderModelSelector conversationId={conversationId} />;
  }

  if (isXS) {
    return null;
  }

  if (isTemporary) {
    return renderClampedTitle(t('nav.routes.chat.temporaryChat'));
  }

  // Route options capture `title` once at navigation time, so `children` can
  // be a stale "Untitled Chat" placeholder after the generated title lands in
  // the store. Prefer the live store title whenever the chat has hydrated.
  // Untitled Work threads keep the short "Work" label so the header is never blank.
  const liveTitle = chat ? displayTitle : children;
  const headerLabel = (liveTitle && String(liveTitle).trim()) || (isWork ? t('navigation.work') : liveTitle);
  const titleNode = renderClampedTitle(
    headerLabel,
    Platform.OS === 'web' && headerLabel ? { title: String(headerLabel) } : undefined,
  );

  // Mobile regular chats can open details from the title. Work already has
  // the same actions on the ⋯ menu, so the title stays a label there.
  if (isWork || !isXS || !isOwner || !chat?.title) {
    // The Mac header is also a window drag target, not a scroll shortcut.
    if (Platform.OS === 'web' && !isXS && conversationId) {
      return (
        <Pressable onPress={() => requestScrollToTop(conversationId)} style={{ maxWidth: Platform.OS === 'macos' ? '100%' : titleMaxWidth, width: titleMaxWidth, minWidth: 0, flexShrink: 1 }}>
          {titleNode}
        </Pressable>
      );
    }
    return titleNode;
  }

  const TriggerComponent = React.forwardRef((triggerProps, ref) => (
    <Pressable
      ref={ref}
      style={[styles.headerContainer, { maxWidth: titleMaxWidth, width: titleMaxWidth }]}
      accessibilityRole="button"
      accessibilityLabel={`${displayTitle || t('navigation.work')}. Open details`}
      {...triggerProps}
    >
      {titleNode}
      <View style={styles.chevronContainer}>
        <Icon
          name={isOpen ? "chevron-down" : "chevron-right"}
          size={11}
          color={colors.text_color_primary}
          styles={styles.chevronIcon}
        />
      </View>
    </Pressable>
  ));

  return (
    <ChatDropdown
      TriggerComponent={TriggerComponent}
      triggerAsChild={Platform.OS === 'web' || Platform.OS === 'macos'}
      conversationId={chat?.id}
      onOpenChange={setIsOpen}
      withChatDetails
    />
  );
});

HeaderTitle.displayName = 'HeaderTitle';

const ChatRouteNavigationHeaderRight = React.memo(({ chat }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { isXS, isSM, windowWidth } = useResponsiveness();
  const navigationWidth = useUIStore(state => getNavigationSidebarWidth(state, windowWidth));
  const contextWidth = useUIStore(state => state.isContextSidebarVisible ? getEffectiveContextSidebarWidth(state, windowWidth) : 0);
  const compactHeaderActions = isXS || isSM
    || (Platform.OS === 'macos' && windowWidth - navigationWidth - contextWidth < 760);
  const isAuthenticated = useAuthPresentation();
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  const isOwner = chat?.isOwner;
  const isWork = !!(chat?.flags?.isCoworkerSession || chat?.coworkerSession);
  const handleNewChat = () => openNewChatComposer(router);

  const {
    isTemporary,
  } = useChat(chat?.id);

  const openContextSidebar = useUIStore(state => state.openContextSidebar);
  const closeContextSidebar = useUIStore(state => state.closeContextSidebar);
  const isContextSidebarVisible = useUIStore(state => state.isContextSidebarVisible);
  const contextSidebarType = useUIStore(state => state.contextSidebarType);
  const contextSidebarProps = useUIStore(state => state.contextSidebarProps);
  const taskId = useCoworkerStore(state => taskIdFromChat(chat) || findTaskIdForConversation(chat?.id, state));
  const localProject = useCoworkerStore(state => state.tasks[taskId]?.localProject);
  const hasLocalProject = !!localProject?.bindingId;
  const browserOpen = useCoworkerStore(state => !!taskId && state.browserViewerTaskId === taskId);
  const openBrowserViewer = useCoworkerStore(state => state.openBrowserViewer);
  const closeBrowserViewer = useCoworkerStore(state => state.closeBrowserViewer);

  const handleOpenChatDetails = () => {
    const type = Platform.OS === 'macos' && hasLocalProject ? 'project-editor' : 'chat-details';
    const isSameChatOpen = isContextSidebarVisible && ['chat-details', 'project-editor'].includes(contextSidebarType) && contextSidebarProps?.conversationId === chat?.id;
    if (isSameChatOpen) {
      closeContextSidebar();
    } else {
      openContextSidebar(type, { conversationId: chat?.id });
    }
  };

  if (!isAuthenticated) {
    return (
      <NavigationBarButtonContainer rightPadding>
        <Button
          text={t('nav.routes.chat.logIn')}
          variant="secondary"
          size={isXS ? 'small' : 'medium'}
          onPress={() => router.push('/login')}
          style={{ marginRight: isXS ? 4 : 6 }}
        />
        <Button
          text={t('nav.routes.chat.signUp')}
          variant="primary"
          size={isXS ? 'small' : 'medium'}
          onPress={() => router.push('/signup')}
        />
      </NavigationBarButtonContainer>
    );
  }

  if (!chat?.title) {
    return (
      <NavigationBarButtonContainer rightPadding>
        <NavigationBarButton
          icon="pen-to-square"
          title={t('nav.routes.chat.newChat')}
          tooltip={t('nav.routes.chat.newChat')}
          tooltipShortcut={chat?.agentId ? undefined : '⌘ ⇧ O'}
          onPress={handleNewChat}
        />
        <Button
          text="Home"
          prependIcon="home"
          variant="secondary"
          size='medium'
          onPress={() => router.push('/app')}
        />
      </NavigationBarButtonContainer>
    );
  }

  const TriggerComponent = React.forwardRef((triggerProps, ref) => (
    <NavigationBarButton
      ref={ref}
      icon="ellipsis"
      title={t('nav.routes.chat.moreOptions')}
      tooltip={t('nav.routes.chat.moreOptions')}
      {...triggerProps}
    />
  ));

  return (
    <View style={{ zIndex: 2 }}>
    <NavigationBarButtonContainer rightPadding glass={isXS}>
      {compactHeaderActions && <NavigationBarButton
        icon="pen-to-square"
        title={t('nav.routes.chat.newChat')}
        tooltip={t('nav.routes.chat.newChat')}
        tooltipShortcut={chat?.agentId ? undefined : '⌘ ⇧ O'}
        onPress={handleNewChat}
      />}
      {Platform.OS === 'macos' && isOwner && isWork && taskId && !isTemporary && !isGhost && !localProject?.revokedAt && <NavigationBarButton
        icon="window-maximize"
        title={browserOpen ? 'Close task browser' : 'Open task browser'}
        tooltip={browserOpen ? 'Close browser' : 'Browser'}
        isActive={browserOpen}
        onPress={() => browserOpen ? closeBrowserViewer() : openBrowserViewer(taskId, { watch: true })}
      />}
      {isOwner && !isTemporary && !compactHeaderActions && isWork && <NavigationBarButton
        icon="table-columns"
        title={t('nav.routes.chat.chatDetails')}
        tooltip={t('nav.routes.chat.chatDetails')}
        onPress={handleOpenChatDetails}
      />}
      {isOwner && (
        <ChatDropdown
          TriggerComponent={TriggerComponent}
          triggerAsChild={Platform.OS === 'web' || Platform.OS === 'macos'}
          conversationId={chat?.id}
          withChatDetails={!isTemporary && (compactHeaderActions || !isWork)}
          withWorkTasks={isWork && !isTemporary && !isGhost}
          withNewChat={isTemporary || !compactHeaderActions}
          withFindInChat={!isTemporary}
          withDeck
        />
      )}
    </NavigationBarButtonContainer>
    </View>
  );
});

ChatRouteNavigationHeaderRight.displayName = 'ChatRouteNavigationHeaderRight';

function LoggedOutHeaderRight() {
  const { t } = useTranslation();
  const router = useRouter();
  const { isXS } = useResponsiveness();
  const isAuthenticated = useAuthPresentation();

  if (isAuthenticated) {
    return null;
  }

  return (
    <NavigationBarButtonContainer rightPadding>
      <Button
        text={t('nav.routes.chat.logIn')}
        variant="secondary"
        size={isXS ? 'small' : 'medium'}
        onPress={() => router.push('/login')}
        style={{ marginRight: isXS ? 4 : 6 }}
      />
      <Button
        text={t('nav.routes.chat.signUp')}
        variant="primary"
        size={isXS ? 'small' : 'medium'}
        onPress={() => router.push('/signup')}
      />
    </NavigationBarButtonContainer>
  )
}

function ChatRouteNavigationHeaderLeft({ chat }) {
  const { isXS } = useResponsiveness();
  return <DefaultRouteNavigationHeaderLeft glass={isXS} />;
}

// New wrapper: resolve chat by id so header reacts to store hydration
const HeaderTitleById = React.memo(({ conversationId, children, initialIsWork = false, preferMobileHeader }) => {
  const { chat, isTemporary } = useChat(conversationId);
  const isLinkedWork = useCoworkerStore((state) => state.taskIds.some((taskId) => {
    const task = state.tasks[taskId];
    const linkedConversation = task?.conversationId || task?.conversation;
    return linkedConversation && String(linkedConversation) === String(conversationId);
  }));
  return (
    <HeaderTitle
      chat={chat}
      isTemporary={isTemporary}
      conversationId={conversationId}
      isLinkedWork={initialIsWork || isLinkedWork}
      preferMobileHeader={preferMobileHeader}
    >
      {children}
    </HeaderTitle>
  );
});
HeaderTitleById.displayName = 'HeaderTitleById';

const ChatHeaderRightById = React.memo(({ conversationId }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const isAuthenticated = useAuthPresentation();
  const { chat } = useChat(conversationId);
  const isLinkedWork = useCoworkerStore((state) => state.taskIds.some((taskId) => {
    const task = state.tasks[taskId];
    const linkedConversation = task?.conversationId || task?.conversation;
    return linkedConversation && String(linkedConversation) === String(conversationId);
  }));

  // If chat not yet loaded: show a sensible fallback so it's never empty
  if (!chat) {
    if (!isAuthenticated) {
      return <LoggedOutHeaderRight />;
    }
    return (
      <NavigationBarButtonContainer rightPadding>
        <NavigationBarButton
          icon="pen-to-square"
          title={t('nav.routes.chat.newChat')}
          tooltipShortcut='⌘ ⇧ O'
          onPress={() => openNewChatComposer(router)}
        />
      </NavigationBarButtonContainer>
    );
  }

  return <ChatRouteNavigationHeaderRight chat={isLinkedWork ? {
    ...chat,
    flags: { ...(chat.flags || {}), isCoworkerSession: true },
  } : chat} />;
});
ChatHeaderRightById.displayName = 'ChatHeaderRightById';

function getChatRouteNavigationOptions(chatOrId, preferMobileHeader) {
  const conversationId = typeof chatOrId === 'string' ? chatOrId : chatOrId?.id;
  const useMobileOverlayHeader = !!preferMobileHeader && (Platform.OS === 'ios' || Platform.OS === 'android');
  const headerBackground = useMobileOverlayHeader
    ? () => null
    : Platform.OS === 'web'
      ? () => <ThemedHeaderBackground />
      : undefined;
  const storedChat = conversationId
    ? useChatStore.getState().chats.find((entry) => entry.id === conversationId)
    : null;
  const initialIsWork = !!(
    chatOrId?.flags?.isCoworkerSession
    || chatOrId?.coworkerSession
    || storedChat?.flags?.isCoworkerSession
    || storedChat?.coworkerSession
  );
  const conversationTitle = (typeof chatOrId === 'object' && chatOrId?.title)
    || storedChat?.title;

  return {
    // Native/web document title (browser tab). The in-app header also shows
    // this name for Work threads, clamped so it cannot cover header actions.
    title: conversationTitle || (conversationId ? (initialIsWork ? 'Work' : 'Chat') : 'Gab AI'),
    headerTitleAlign: 'center',
    headerTransparent: useMobileOverlayHeader,
    headerBackground,
    headerShadowVisible: false,
    headerStyle: useMobileOverlayHeader ? {
      backgroundColor: 'transparent',
      borderBottomWidth: 0,
      borderBottomColor: 'transparent',
      shadowColor: 'transparent',
      elevation: 0,
    } : undefined,
    headerTitleContainerStyle: {
      overflow: 'hidden',
      paddingHorizontal: 8,
    },
    headerTitle: ({ children }) => (
      <HeaderTitleById conversationId={conversationId} initialIsWork={initialIsWork} preferMobileHeader={!!preferMobileHeader}>
        {children}
      </HeaderTitleById>
    ),
    headerBackVisible: false,
    headerLeft: () => <ChatRouteNavigationHeaderLeft conversationId={conversationId} />,
    headerRight: () => <ChatHeaderRightById conversationId={conversationId} />
  };
}

export default getChatRouteNavigationOptions;
